import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Wallet, X, MessageCircle } from 'lucide-react';
import { scanMinimumFormula } from '../utils/scanMinimum';
import ScanMinimumInfo from './ScanMinimumInfo';
import './ExpiredModal.css';

const TELEGRAM_LINK = 'https://t.me/mrbuslov';

function LimitModal() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [params, setParams] = useState(null);

  useEffect(() => {
    const handleLimitReached = (event) => setParams(event.detail || {});
    window.addEventListener('char-limit-reached', handleLimitReached);
    return () => window.removeEventListener('char-limit-reached', handleLimitReached);
  }, []);

  if (!params) return null;

  const close = () => setParams(null);
  const fmt = (value) => Number(value).toLocaleString(i18n.language);
  const needed = params.document_chars;
  const remaining = params.remaining_chars;
  // The reset line only when the coming top-up adds free characters.
  const resetDate = params.free_resets_at && params.free_topped_up_to > params.free_remaining
    ? new Date(params.free_resets_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })
    : null;

  return (
    <div className="expired-modal-overlay">
      <div className="expired-modal">
        <button className="expired-modal-close" onClick={close}>
          <X size={20} />
        </button>

        <div className="expired-modal-icon expired-modal-icon-accent">
          <Wallet size={48} />
        </div>

        <h2 className="expired-modal-title">{t('charLimit.title')}</h2>
        {needed == null && (
          <p className="expired-modal-message">{t('charLimit.message', { remaining: fmt(remaining ?? 0) })}</p>
        )}

        {needed != null && (
          <div className="limit-modal-figures">
            <div>
              <span className="limit-modal-figure">{fmt(needed)}</span>
              <span className="limit-modal-label">{t('charLimit.needed')}</span>
            </div>
            <div>
              <span className="limit-modal-figure">{fmt(remaining ?? 0)}</span>
              <span className="limit-modal-label">{t('charLimit.remaining')}</span>
            </div>
          </div>
        )}

        {params.scan_min_chars_per_page != null && (
          <p className="limit-modal-reset">
            {scanMinimumFormula(t, params, i18n.language)} = {fmt(needed)}
            <ScanMinimumInfo />
          </p>
        )}

        {resetDate && (
          <p className="limit-modal-reset">
            {t('charLimit.freeResets', { limit: fmt(params.free_topped_up_to), date: resetDate })}
          </p>
        )}

        <button
          className="expired-modal-button limit-modal-topup"
          onClick={() => { close(); navigate('/usage'); }}
        >
          <Wallet size={20} />
          {t('charLimit.topUpButton')}
        </button>

        <p className="limit-modal-hint">
          {t(params.scan_min_chars_per_page != null ? 'charLimit.fewerPagesHint' : 'charLimit.smallerDocumentHint')}
        </p>

        <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="limit-modal-telegram">
          <MessageCircle size={16} />
          {t('charLimit.contactButton')}
        </a>

        <button className="expired-modal-dismiss" onClick={close}>
          {t('charLimit.dismiss')}
        </button>
      </div>
    </div>
  );
}

export default LimitModal;
